import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'
import { z } from 'zod'

import { AuthErrorBanner, AuthTitle } from '~/components/auth-shell/AuthFormPrimitives'
import {
  SetPasswordForm,
  type SetPasswordCopy,
} from '~/components/auth-shell/SetPasswordForm'

/**
 * `/authentication/set-password` — admin invite password set surface.
 *
 * Triggered by an emailed invite link from a super_admin granting platform
 * admin access. Reads `?token=` and `?email=` from the URL, consumes the
 * Supabase invite token via `verifyOtp({ type: 'invite' })`, sets the user's
 * password via `updateUser`, then routes to `/admin`.
 *
 * Shares the form body with `authentication.set-cms-password.tsx` — only the
 * copy + post-submit redirect differ between admin and CMS variants.
 */

const searchSchema = z.object({
  token: z.string().optional(),
  email: z.string().email().optional(),
})

const ADMIN_COPY: SetPasswordCopy = {
  title: 'Welcome to Kairos Admin',
  subtitle: 'Set your password to activate your account.',
  cta: 'Activate account',
  redirectTo: '/admin',
}

export const Route = createFileRoute('/authentication/set-password')({
  validateSearch: searchSchema,
  component: SetPasswordPage,
})

function SetPasswordPage(): ReactNode {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const [bannerError, setBannerError] = useState<string | null>(null)

  return (
    <>
      {!search.token ? (
        <InvalidTokenState />
      ) : (
        <SetPasswordForm
          copy={ADMIN_COPY}
          token={search.token}
          email={search.email}
          bannerError={bannerError}
          onError={setBannerError}
          onSuccess={() => {
            setBannerError(null)
            void navigate({ to: ADMIN_COPY.redirectTo })
          }}
        />
      )}
    </>
  )
}

function InvalidTokenState(): ReactNode {
  return (
    <>
      <AuthTitle
        title="This invite is invalid or expired"
        subtitle="Reach out to the platform owner who sent the invite to get a fresh link."
      />
      <AuthErrorBanner>
        We couldn&apos;t find an activation token in this URL.
      </AuthErrorBanner>
    </>
  )
}
